// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import xLoadingOverlay from "@conklin/App/Handlers/XNG/LoadingOverlay";
import { getAssociatedBillTo } from "@conklin/App/Services/AllShipTosService";
import { setHasCompletedCustomerSelection } from "@conklin/App/Utilities/changeCustomerSelectionStorage";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { GetBillTosApiParameter, GetShipTosApiParameter } from "@insite/client-framework/Services/CustomersService";
import { FulfillmentMethod, Session } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import changeCustomerContext from "@insite/client-framework/Store/Context/Handlers/ChangeCustomerContext";
import { getBillTosDataView } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTos from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTos";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getReturnUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import loadShipTos from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTos";
import { getShipTosDataView } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { BillToModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import ChangeCustomerBillToSelector, {
    ChangeCustomerBillToSelectorStyles,
} from "@insite/content-library/Widgets/ChangeCustomer/ChangeCustomerBillToSelector";
import ChangeCustomerFulfillmentMethodSelector from "@insite/content-library/Widgets/ChangeCustomer/ChangeCustomerFulfillmentMethodSelector";
import ChangeCustomerShipToSelector, {
    ChangeCustomerShipToSelectorStyles,
} from "@insite/content-library/Widgets/ChangeCustomer/ChangeCustomerShipToSelector";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: ChangeCustomerSelectCustomerContainerStyles;
    billTosParameter: GetBillTosApiParameter;
    setBillTosParameter: (parameter: GetBillTosApiParameter) => void;
    shipTosParameter: GetShipTosApiParameter;
    setShipTosParameter: (parameter: GetShipTosApiParameter) => void;
    defaultCustomerChangedMessage?: string;
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const {
        accountSettings: { requireSelectCustomerOnSignIn, enableWarehousePickup },
        customerSettings: { allowCreateNewShipToAddress },
    } = getSettingsCollection(state);
    const {
        fulfillmentMethod,
        pickUpWarehouse,
        hasDefaultCustomer,
        displayChangeCustomerLink,

        billToId: sessionBillToId,
        shipToId: sessionShipToId,
    } = getSession(state);
    return {
        cartState: getCurrentCartState(state),
        shipTosDataView: getShipTosDataView(state, props.shipTosParameter),
        billTosDataView: getBillTosDataView(state, props.billTosParameter),
        fulfillmentMethod,
        pickUpWarehouse,
        sessionBillToId,
        sessionShipToId,
        enableWarehousePickup,
        allowCreateNewShipToAddress,
        dashboardUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
        addressesUrl: getPageLinkByPageType(state, "AddressesPage")?.url,
        checkoutShippingUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        checkoutReviewAndSubmitUrl: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")?.url,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        homeUrl: getPageLinkByPageType(state, "HomePage")?.url,
        returnUrl: getReturnUrl(state),
        canSetDefaultCustomer: !requireSelectCustomerOnSignIn && !hasDefaultCustomer,
        displayChangeCustomerLink,
    };
};

const mapDispatchToProps = {
    loadBillTos,
    loadShipTos,
    changeCustomerContext,
    // XNG-Changes: adding overlay spinner
    xLoadingOverlay,
};

type Props = HasHistory & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface ChangeCustomerSelectCustomerContainerStyles {
    container?: GridContainerProps;
    billToSelectorGridItem?: GridItemProps;
    billToSelector?: ChangeCustomerBillToSelectorStyles;
    billingAddressTitle?: TypographyPresentationProps;
    billingAddressPlaceholder?: TypographyPresentationProps;
    billingAddressDisplay?: AddressInfoDisplayStyles;
    fulfillmentMethodSelectorGridItem?: GridItemProps;
    shipToSelectorGridItem?: GridItemProps;
    shipToSelector?: ChangeCustomerShipToSelectorStyles;
    useDefaultCustomerGridItem?: GridItemProps;
    useDefaultCustomerCheckboxGroup?: CheckboxGroupProps;
    useDefaultCustomerCheckbox?: CheckboxPresentationProps;
    useDefaultCustomerDescriptionVisible?: InjectableCss;
    useDefaultCustomerDescriptionHidden?: InjectableCss;
    buttonsGridItem?: GridItemProps;
    continueButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
    requiredSelectionModal?: TwoButtonModalStyles;
}

export const changeCustomerSelectCustomerContainerStyles: ChangeCustomerSelectCustomerContainerStyles = {
    billToSelectorGridItem: {
        width: 12,
    },
    billingAddressTitle: {},
    billingAddressPlaceholder: {},
    fulfillmentMethodSelectorGridItem: {
        width: 12,
    },
    shipToSelectorGridItem: {
        width: 12,
    },
    useDefaultCustomerGridItem: {
        width: 12,
    },
    useDefaultCustomerDescriptionVisible: {
        css: css`
            margin-top: 0.75rem;
        `,
    },
    useDefaultCustomerDescriptionHidden: {
        css: css`
            visibility: hidden;
        `,
    },
    buttonsGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    cancelButton: {
        variant: "secondary",
        css: css`
            margin-right: 15px;
        `,
    },
    requiredSelectionModal: {},
};

const DescriptionWrapper = getStyledWrapper("div");

const ChangeCustomerSelectCustomerContainer = ({
    extendedStyles,
    allowCreateNewShipToAddress,
    enableWarehousePickup,
    pickUpWarehouse,
    cartState,
    shipTosDataView,
    billTosDataView,
    billTosParameter,
    setBillTosParameter,
    shipTosParameter,
    setShipTosParameter,
    fulfillmentMethod,
    loadBillTos,
    loadShipTos,
    changeCustomerContext,
    returnUrl,
    homeUrl,
    canSetDefaultCustomer,
    defaultCustomerChangedMessage,
    sessionBillToId,
    sessionShipToId,
    // XNG-Changes
    xLoadingOverlay,
}: Props) => {
    const [styles] = useState(() => mergeToNew(changeCustomerSelectCustomerContainerStyles, extendedStyles));
    const [billTo, setBillTo] = useState<BillToModel | undefined>(undefined);

    const [shipToSearchText, setShipToSearchText] = useState("");
    const [shipTo, setShipTo] = useState<ShipToModel | undefined>(undefined);
    // XNG-Changes: keep the user on this page until they select or lock defaults
    const [isRequiredSelectionModalOpen, setIsRequiredSelectionModalOpen] = useState(false);
    const hasCompletedSelection = useRef(false);

    const [noShipToAndCantCreate, setNoShipToAndCantCreate] = useState<boolean>(
        !allowCreateNewShipToAddress && !shipToSearchText && shipTosDataView.value?.length === 0,
    );

    const [useDefaultCustomer, setUseDefaultCustomer] = useState(false);

    const relatedCustomers = [...(shipTosDataView.value || []), ...(billTosDataView.value || [])];

    // const billToSelectedHandler = (billTo: BillToModel) => {
    //     setBillTo(billTo);
    //     setShipTo(undefined);
    //     setShipTosParameter({
    //         ...shipTosParameter,
    //         billToId: billTo.id,
    //     });
    // };

    // const shipToSelectedHandler = (shipTo: ShipToModel) => {
    //     setShipTo(shipTo);
    // };

    // XNG-Changes: bill-to is the parent of the selected ship-to, not a separate dropdown
    const shipToSelectedHandler = (selectedShipTo: ShipToModel) => {
        setShipTo(selectedShipTo);
        const associatedBillTo = getAssociatedBillTo(selectedShipTo, relatedCustomers);
        setBillTo(associatedBillTo);
        if (
            !associatedBillTo &&
            selectedShipTo.customerNumber &&
            billTosParameter.filter !== selectedShipTo.customerNumber
        ) {
            setBillTosParameter({
                ...billTosParameter,
                filter: selectedShipTo.customerNumber,
                page: 1,
            });
        }
    };

    const useDefaultCustomerHandler = (_: React.SyntheticEvent<Element, Event>, value: boolean) => {
        setUseDefaultCustomer(value);
    };

    const handleContinueClicked = () => {
        if (!billTo || !shipTo || !cartState.value) {
            return;
        }

        // XNG-Changes: loading spinner
        hasCompletedSelection.current = true;
        setHasCompletedCustomerSelection(true);
        xLoadingOverlay({ key: "add", id: "change_customer_context" });
        changeCustomerContext({
            billToId: billTo.id,
            shipToId: shipTo.isNew ? "" : shipTo.id,
            fulfillmentMethod,
            pickUpWarehouse: fulfillmentMethod === FulfillmentMethod.PickUp ? pickUpWarehouse : null,
            returnUrl,
            isDefault: useDefaultCustomer,
            onSuccess: () => {
                xLoadingOverlay({ key: "clear", id: "change_customer_context" });
            },
            onError: () => {
                hasCompletedSelection.current = false;
                setHasCompletedCustomerSelection(false);
                xLoadingOverlay({ key: "clear", id: "change_customer_context" });
            },
        });
    };

    // XNG-Changes: cancel/skip must confirm selection or lock to defaults
    const handleCancelClicked = () => {
        // window.location.href = returnUrl || homeUrl || "/";
        setIsRequiredSelectionModalOpen(true);
    };

    const handleStayAndSelect = () => {
        setIsRequiredSelectionModalOpen(false);
    };

    const handleUseDefaults = () => {
        if (!sessionBillToId || !sessionShipToId || !cartState.value) {
            setIsRequiredSelectionModalOpen(false);
            return;
        }

        hasCompletedSelection.current = true;
        setHasCompletedCustomerSelection(true);
        setIsRequiredSelectionModalOpen(false);
        xLoadingOverlay({ key: "add", id: "change_customer_context" });
        changeCustomerContext({
            billToId: sessionBillToId,
            shipToId: sessionShipToId,
            fulfillmentMethod,
            pickUpWarehouse: fulfillmentMethod === FulfillmentMethod.PickUp ? pickUpWarehouse : null,
            returnUrl,
            isDefault: true,
            onSuccess: () => {
                xLoadingOverlay({ key: "clear", id: "change_customer_context" });
            },
            onError: () => {
                hasCompletedSelection.current = false;
                setHasCompletedCustomerSelection(false);
                xLoadingOverlay({ key: "clear", id: "change_customer_context" });
            },
        });
    };

    useEffect(() => {
        setNoShipToAndCantCreate(
            !allowCreateNewShipToAddress && !shipToSearchText && shipTosDataView.value?.length === 0,
        );
    }, [allowCreateNewShipToAddress, shipToSearchText, shipTosDataView]);

    // Trigger Search for Bill Tos
    // useEffect(() => {
    //     if (!billTosDataView.value && !billTosDataView.isLoading) {
    //         loadBillTos(billTosParameter);
    //     }
    //
    //     // XNG-Changes
    //     if (!shipTosDataView.value && !shipTosDataView.isLoading) {
    //         loadShipTos({ ...shipTosParameter });
    //     }
    // }, [billTosParameter, shipTosParameter]);

    // XNG-Changes: load assigned ship-tos from api/cmi/shipTos (no billToId)
    useEffect(() => {
        if (!shipTosDataView.value && !shipTosDataView.isLoading) {
            loadShipTos(shipTosParameter);
        }
    }, [shipTosParameter]);

    // XNG-Changes: load assigned bill-tos so the parent billing address can be displayed
    useEffect(() => {
        if (!billTosDataView.value && !billTosDataView.isLoading) {
            loadBillTos(billTosParameter);
        }
    }, [billTosParameter]);

    useEffect(() => {
        if (!shipTo) {
            return;
        }

        const associatedBillTo = getAssociatedBillTo(shipTo, relatedCustomers);
        if (associatedBillTo) {
            setBillTo(associatedBillTo);
        }
    }, [shipTo, shipTosDataView.value, billTosDataView.value]);

    // XNG-Changes: block leaving this page until a ship-to/bill-to is chosen or defaults are locked
    useEffect(() => {
        const isLeavingChangeCustomer = (href: string) => {
            try {
                const url = new URL(href, window.location.origin);
                return url.origin !== window.location.origin || url.pathname !== window.location.pathname;
            } catch {
                return false;
            }
        };

        const onDocumentClick = (event: MouseEvent) => {
            if (hasCompletedSelection.current) {
                return;
            }

            const target = event.target as HTMLElement | null;
            const anchor = target?.closest("a");
            if (!anchor) {
                return;
            }

            const href = anchor.getAttribute("href");
            // eslint-disable-next-line no-script-url
            if (!href || href.startsWith("#") || href.toLowerCase().startsWith("javascript:")) {
                return;
            }

            if (!isLeavingChangeCustomer(href)) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            setIsRequiredSelectionModalOpen(true);
        };

        const onPopState = () => {
            if (hasCompletedSelection.current) {
                return;
            }

            window.history.pushState(null, "", window.location.href);
            setIsRequiredSelectionModalOpen(true);
        };

        window.history.pushState(null, "", window.location.href);
        document.addEventListener("click", onDocumentClick, true);
        window.addEventListener("popstate", onPopState);

        return () => {
            document.removeEventListener("click", onDocumentClick, true);
            window.removeEventListener("popstate", onPopState);
        };
    }, []);

    const useDefaultCustomerDescriptionStyles = useDefaultCustomer
        ? styles.useDefaultCustomerDescriptionVisible
        : styles.useDefaultCustomerDescriptionHidden;

    return (
        <>
            <GridContainer {...styles.container}>
                <GridItem {...styles.fulfillmentMethodSelectorGridItem}>
                    <ChangeCustomerFulfillmentMethodSelector />
                </GridItem>
                {!noShipToAndCantCreate && (
                    <GridItem {...styles.shipToSelectorGridItem}>
                        <ChangeCustomerShipToSelector
                            extendedStyles={styles.shipToSelector}
                            parameter={shipTosParameter}
                            setParameter={setShipTosParameter}
                            searchText={shipToSearchText}
                            setSearchText={setShipToSearchText}
                            enableWarehousePickup={enableWarehousePickup}
                            fulfillmentMethod={fulfillmentMethod}
                            onSelect={shipToSelectedHandler}
                            // billToId={billTo?.id}
                            shipTo={shipTo}
                            // XNG-Changes: do not pass billToId so CMI ship-tos stay loaded
                            isLoading={shipTosDataView.isLoading}
                        />
                    </GridItem>
                )}
                {/* <GridItem {...styles.fulfillmentMethodSelectorGridItem}>
                <ChangeCustomerFulfillmentMethodSelector />
            </GridItem> */}
                {/* {shipTo && (
                <GridItem {...styles.billToSelectorGridItem}>
                    <ChangeCustomerBillToSelector
                        extendedStyles={styles.billToSelector}
                        parameter={billTosParameter}
                        setParameter={setBillTosParameter}
                        noShipToAndCantCreate={noShipToAndCantCreate}
                        onSelect={billToSelectedHandler}
                        billTo={billTo}
                        // Xng-Changes
                        isLoading={billTosDataView.isLoading}
                    />
                </GridItem>
            )} */}
                {/* XNG-Changes: show the associated bill-to address instead of the bill-to dropdown */}
                <GridItem {...styles.billToSelectorGridItem}>
                    <Typography {...styles.billingAddressTitle}>{translate("Billing Address")}</Typography>
                    {billTo ? (
                        <AddressInfoDisplay
                            firstName={billTo.firstName}
                            lastName={billTo.lastName}
                            companyName={billTo.companyName || billTo.customerName}
                            attention={billTo.attention}
                            address1={billTo.address1}
                            address2={billTo.address2}
                            address3={billTo.address3}
                            address4={billTo.address4}
                            city={billTo.city}
                            state={billTo.state?.abbreviation || billTo.state?.name}
                            postalCode={billTo.postalCode}
                            country={billTo.country?.abbreviation || billTo.country?.name}
                            phone={billTo.phone}
                            fax={billTo.fax}
                            email={billTo.email}
                            extendedStyles={styles.billingAddressDisplay}
                        />
                    ) : (
                        <Typography {...styles.billingAddressPlaceholder}>
                            {translate("Select a ship-to address to see billing.")}
                        </Typography>
                    )}
                </GridItem>
                {canSetDefaultCustomer && billTo && shipTo && !shipTo.isNew && (
                    <GridItem {...styles.useDefaultCustomerGridItem}>
                        <CheckboxGroup {...styles.useDefaultCustomerCheckboxGroup}>
                            <Checkbox
                                {...styles.useDefaultCustomerCheckbox}
                                aria-describedby="setDefaultCustomer-description"
                                checked={useDefaultCustomer}
                                data-test-selector="changeCustomer_useDefaultCustomer"
                                onChange={useDefaultCustomerHandler}
                            >
                                {siteMessage("DefaultCustomer_SetToUser")}
                            </Checkbox>
                            <DescriptionWrapper
                                {...useDefaultCustomerDescriptionStyles}
                                id="setDefaultCustomer-description"
                            >
                                {defaultCustomerChangedMessage ||
                                    "If you would like to change your defaults in the future, please visit your Account Settings page."}
                            </DescriptionWrapper>
                        </CheckboxGroup>
                    </GridItem>
                )}
                <GridItem {...styles.buttonsGridItem}>
                    <Button {...styles.cancelButton} onClick={handleCancelClicked} aria-label="Cancel">
                        {translate("Cancel")}
                    </Button>
                    <Button
                        {...styles.continueButton}
                        onClick={handleContinueClicked}
                        disabled={!billTo || !shipTo || !cartState.value}
                        data-test-selector="changeCustomer_continue"
                        aria-label="Continue"
                    >
                        {translate("Continue")}
                    </Button>
                </GridItem>
            </GridContainer>
            <TwoButtonModal
                extendedStyles={styles.requiredSelectionModal}
                modalIsOpen={isRequiredSelectionModalOpen}
                headlineText={translate("Ship-To / Bill-To Required")}
                messageText={translate(
                    "A Ship-To and Bill-To are required to log in. You must make a selection to continue. If you do not make a selection, you will be locked to your default customer.",
                )}
                cancelButtonText={translate("Select Address")}
                submitButtonText={translate("Use My Defaults")}
                onCancel={handleStayAndSelect}
                onSubmit={handleUseDefaults}
                onClose={handleStayAndSelect}
                dataTestSelector="changeCustomer_requiredSelectionModal"
                cancelTestSelector="changeCustomer_selectAddress"
                submitTestSelector="changeCustomer_useDefaults"
            />
        </>
    );
};

type GetCustomerContinueReturnUrlType = (props: {
    session: Session;
    dashboardUrl?: string;
    checkoutShippingUrl?: string;
    canCheckOut: boolean;
    cartUrl?: string;
    canBypassCheckoutAddress: boolean;
    checkoutReviewAndSubmitUrl?: string;
    homeUrl?: string;
}) => string;

export const getCustomerContinueReturnUrl: GetCustomerContinueReturnUrlType = ({
    session,
    dashboardUrl,
    checkoutShippingUrl,
    canCheckOut,
    cartUrl,
    canBypassCheckoutAddress,
    checkoutReviewAndSubmitUrl,
    homeUrl,
}) => {
    let returnUrl: string | undefined = "";
    if (session.dashboardIsHomepage) {
        returnUrl = dashboardUrl;
    } else if (session.customLandingPage) {
        returnUrl = session.customLandingPage;
    }

    if (returnUrl?.toLowerCase() === checkoutShippingUrl?.toLowerCase()) {
        if (!canCheckOut || session.isRestrictedProductExistInCart) {
            returnUrl = cartUrl;
        } else if (canBypassCheckoutAddress) {
            returnUrl = checkoutReviewAndSubmitUrl;
        }
    }

    // This forces the undefined typing to returnUrl or "/"
    returnUrl = returnUrl || homeUrl || "/";

    return returnUrl;
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(ChangeCustomerSelectCustomerContainer));
